import { Container, ContainerChild, Sprite, Texture } from "pixi.js";

import type {
  IStudioWidget,
  IStudioWidgetDateTime,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import { TimerService } from "../Services/TimerService";

import {
  computeClockParts,
  renderClockDesignSvg,
  type ClockDesign,
  type ClockParts,
} from "../../../../data/clockDesignsCatalog";

import {
  ensureDesignFontsReady,
  rasterizeSvgToTexture,
} from "./svgTextureRaster";

/**
 * Design-store render strategy for the Clock (DATE_TIME) widget — the
 * time-of-day sibling of the Countdown `DesignRenderer`. When the widget's
 * `options.designId` resolves to a {@link ClockDesign}, this renders that
 * design's CARD SVG (from the local catalog) as a single Pixi sprite stretched
 * to the widget bounds, kept live by re-rasterising the texture only when the
 * displayed time key actually changes — driven by the same `TimerService` 1s
 * tick the legacy clock text uses (never every frame).
 *
 * One instance per widget (the parent `DateTimeWidgetRenderer` keeps a Map
 * keyed by widget id), so multiple clocks with different designs coexist.
 */
export class ClockDesignRenderer {
  private timerCallback: ((time: number) => void) | null = null;

  private container: Container | null = null;

  private sprite: Sprite | null = null;

  private destWidth = 1;

  private destHeight = 1;

  private renderScale = 1;

  /** Last time key baked into the current texture. */
  private lastKey: string | null = null;

  /** Texture currently shown — destroyed before being replaced. */
  private texture: Texture | null = null;

  /** Bumped per re-raster so a slow async decode can't apply out of order. */
  private generation = 0;

  private destroyed = false;

  private options: IStudioWidgetDateTime["options"] | null = null;

  constructor(private readonly design: ClockDesign) {}

  render(
    widget: IStudioWidget,
    parentContainer: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const options = (widget as IStudioWidgetDateTime).options;
    this.options = options;

    this.container = new Container();
    parentContainer.addChild(this.container);

    this.destWidth = Math.max(1, destination.width);
    this.destHeight = Math.max(1, destination.height);

    const scaleForBounds = Math.max(
      1,
      this.destWidth / this.design.cardWidth,
      this.destHeight / this.design.cardHeight,
    );
    this.renderScale = Math.min(scaleForBounds, 3);

    const sprite = new Sprite(Texture.EMPTY);
    sprite.width = this.destWidth;
    sprite.height = this.destHeight;
    sprite.position.set(0, 0);
    this.container.addChild(sprite as unknown as ContainerChild);
    this.sprite = sprite;

    // Draw the first frame immediately, then redraw once the design's Google
    // fonts are decoded so the real typeface (not a fallback) is baked in.
    void this.rerasterize(this.computeParts(options));
    void ensureDesignFontsReady(this.design.fonts).then(() => {
      if (this.destroyed) return;
      this.lastKey = null;
      if (this.options) void this.rerasterize(this.computeParts(this.options));
    });

    const update = (_now: number) => {
      if (this.destroyed || !this.options) return;
      const parts = this.computeParts(this.options);
      if (this.keyOf(parts) !== this.lastKey) {
        void this.rerasterize(parts);
      }
    };

    update(Date.now());
    this.timerCallback = update;
    TimerService.getInstance().subscribe(update);
  }

  private computeParts(options: IStudioWidgetDateTime["options"]): ClockParts {
    return computeClockParts(new Date(), {
      timeZone: options.timezone,
      hour12: options.timeFormat === "12h",
    });
  }

  /**
   * The minimal key that decides a re-raster: the time + (seconds only when the
   * design shows them) + the date/zone parts that can change. Non-seconds
   * designs therefore re-raster ~once a minute, not every tick.
   */
  private keyOf(p: ClockParts): string {
    return [
      p.time,
      this.design.usesSeconds ? p.seconds : "",
      p.ampm,
      p.weekdayShort,
      p.day2,
      p.monthShort,
      p.year,
      p.gmt,
    ].join("|");
  }

  private async rerasterize(parts: ClockParts): Promise<void> {
    if (this.destroyed || !this.sprite) return;

    this.lastKey = this.keyOf(parts);
    const gen = ++this.generation;

    const svg = renderClockDesignSvg(this.design, parts);

    try {
      const texture = await rasterizeSvgToTexture(
        svg,
        this.design.cardWidth,
        this.design.cardHeight,
        this.renderScale,
      );

      if (
        this.destroyed ||
        gen !== this.generation ||
        !this.sprite ||
        this.sprite.destroyed
      ) {
        texture.destroy(true);
        return;
      }

      const old = this.texture;
      this.texture = texture;
      this.sprite.texture = texture;
      this.sprite.width = this.destWidth;
      this.sprite.height = this.destHeight;
      if (old && old !== Texture.EMPTY) old.destroy(true);
    } catch {
      if (!this.destroyed && gen === this.generation) {
        this.lastKey = null;
      }
    }
  }

  destroy(): void {
    this.destroyed = true;

    if (this.timerCallback) {
      TimerService.getInstance().unsubscribe(this.timerCallback);
      this.timerCallback = null;
    }
    if (this.texture && this.texture !== Texture.EMPTY) {
      try {
        this.texture.destroy(true);
      } catch {
        /* best-effort */
      }
    }
    this.texture = null;
    this.sprite = null;
    this.options = null;

    if (this.container) {
      this.container.destroy({ children: true });
      this.container = null;
    }
  }
}
